import { z } from 'zod'

/**
 * The envelope every endpoint returns, success or failure.
 *
 * Kept as a hand-written generic interface rather than a Zod-inferred type because callers
 * need `ApiResponse<Order>` to be writable directly. `apiResponseSchema()` below produces the
 * matching runtime validator when a response actually needs parsing.
 */
export interface ApiResponse<T> {
  success: boolean
  data?: T
  error?: string
  meta?: {
    total: number
    page: number
    limit: number
  }
}

export const paginationMetaSchema = z.object({
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
})

export type PaginationMeta = z.infer<typeof paginationMetaSchema>

/** Runtime validator for the envelope around a given payload schema. */
export const apiResponseSchema = <T extends z.ZodType>(data: T) =>
  z.object({
    success: z.boolean(),
    data: data.optional(),
    error: z.string().optional(),
    meta: paginationMetaSchema.optional(),
  })

/** Query parameters accepted by every list endpoint. */
export const paginationQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(200).default(50),
})

export type PaginationQuery = z.infer<typeof paginationQuerySchema>
